/**
 * TokenProvider — abstracts over platform-specific token storage.
 */

import { isReactNativeApp } from '@amzn/jwo-store-management-platform-components'
import type { OAuthProviderConfig, StoredTokens } from './web/types'

/**
 * Outcome of an on-demand token refresh.
 * - refreshed: a new access token was issued.
 * - failed: the session cannot be refreshed (e.g. a revoked or expired refresh token), so the user must sign in again.
 * - transient: the refresh could not complete right now (network failure, token endpoint 5xx); the session is kept and
 *   a later refresh can still succeed.
 * - unsupported: the platform does not refresh on demand (React Native, where the native shell owns refresh).
 */
export type TokenRefreshResult =
    | { kind: 'refreshed'; accessToken: string }
    | { kind: 'failed' }
    | { kind: 'transient' }
    | { kind: 'unsupported' }

// tslint:disable-next-line: interface-name
export interface TokenProvider {
    getAccessToken (): Promise<string>
    isAuthenticated (): Promise<boolean>
    storeTokens (tokens: StoredTokens): void
    getProviderConfig (): OAuthProviderConfig | null
    setProviderConfig (config: OAuthProviderConfig): void
    clear (): Promise<void>
    /**
     * Refresh the access token now, regardless of its local expiry. Optional: providers whose host owns
     * refresh (React Native) do not implement it. timeoutMs caps the refresh request; past it the request is
     * aborted and the result is 'transient'.
     */
    forceRefresh? (options?: { timeoutMs?: number }): Promise<TokenRefreshResult>
    /**
     * Revoke the refresh token at the identity provider on sign-out. Best effort, never rejects. Optional: React
     * Native's native shell owns its own sign-out.
     */
    revoke? (): Promise<void>
}

export function createTokenProvider (): TokenProvider {
    if (isReactNativeApp()) {
        const { NativeBridgeTokenProvider } = require('./native/NativeBridgeTokenProvider')
        return new NativeBridgeTokenProvider()
    }
    const { WebTokenProvider } = require('./web/WebTokenProvider')
    return new WebTokenProvider()
}
